/**
 * Guest / Account OIDC `?error=` and API recovery copy (Wave 3.4 / G16).
 * RegisterScreen and useSignInScreen share this module.
 */

import { CustomerAuthErrorCodes } from './authErrorCodes.js';
import { resolveOidcConflictMessageKey } from './oidcConflictRecovery.js';

export interface ResolveOidcRecoveryMessageOptions {
  /** Link-mode / Account surfaces use authenticated conflict copy. */
  readonly authenticated?: boolean;
}

function resolveOidcRecoveryMessageKey(
  code: string,
  authenticated: boolean,
): string | undefined {
  const conflictKey = resolveOidcConflictMessageKey(code, authenticated);
  if (conflictKey !== undefined) {
    return conflictKey;
  }

  if (
    code === CustomerAuthErrorCodes.OIDC_ACCOUNT_LINK_REQUIRED ||
    code === CustomerAuthErrorCodes.OIDC_ACCOUNT_LINK_REQUIRED_QUERY ||
    code === 'oidc_account_link_required'
  ) {
    return 'auth.oidc.error.emailExistsLinkInAccount';
  }

  if (
    code === CustomerAuthErrorCodes.OIDC_PHONE_UNAVAILABLE ||
    code === 'oidc_phone_unavailable'
  ) {
    return 'auth.oidc.error.phoneUnavailable';
  }

  if (
    code === 'oidc_phone_gate_incomplete' ||
    code === 'OIDC_PHONE_GATE_INCOMPLETE' ||
    code === 'phone_gate_incomplete'
  ) {
    return 'auth.oidc.error.phoneGateIncomplete';
  }

  if (code === 'CSRF_FAILED' || code === 'csrf_failed' || code === 'oidc_csrf_failed') {
    return 'auth.oidc.error.csrfFailed';
  }

  if (
    code === 'RATE_LIMITED' ||
    code === 'rate_limited' ||
    code === 'HTTP_STORM_DETECTED' ||
    code === '429'
  ) {
    return 'auth.oidc.error.rateLimited';
  }

  if (
    code === 'oidc_code_missing' ||
    code === 'oidc_token_invalid' ||
    code === 'oidc_state_invalid' ||
    code === CustomerAuthErrorCodes.TOKEN_EXPIRED ||
    code === 'TOKEN_EXPIRED'
  ) {
    return 'auth.oidc.error.expiredToken';
  }

  if (code === 'oidc_consent_required' || code === 'CONSENTS_REQUIRED') {
    // Guest sign-in: register-only ticks — never tell the user to tick on sign-in.
    // Authenticated Account link keeps auth.oidc.consentsRequired.
    return authenticated
      ? 'auth.oidc.consentsRequired'
      : 'auth.oidc.error.consentsRequired';
  }

  if (
    code === 'oidc_not_enabled' ||
    code === 'oidc_callback_failed' ||
    code === 'oidc_start_failed' ||
    code === 'oidc_discovery_failed' ||
    code === 'oidc_account_ineligible' ||
    code === 'oidc_tenant_invalid'
  ) {
    return 'auth.oidc.error.unavailable';
  }

  return undefined;
}

export function resolveOidcRecoveryMessage(
  t: (key: string) => string,
  code: string | null,
  options: ResolveOidcRecoveryMessageOptions = {},
): string | undefined {
  if (code === null || code.length === 0) {
    return undefined;
  }
  const key = resolveOidcRecoveryMessageKey(code, options.authenticated === true);
  return key !== undefined ? t(key) : undefined;
}
